import {
  Button,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
} from "reactstrap";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { createMaterial, updateMaterial } from "../services/MaterialService";
import { Material } from "../types/material.types";
import schema from "./schema";

type Props = {
  show: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /** When null, create. When set, edit that material. */
  editingMaterial: Material | null;
};

export function MaterialCreateModal({
  show,
  onClose,
  onSuccess,
  editingMaterial,
}: Props) {
  const isEdit = editingMaterial != null;

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: editingMaterial?.name ?? "",
      isActive: editingMaterial?.isActive ?? true,
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      try {
        if (isEdit && editingMaterial) {
          const res = await updateMaterial(editingMaterial.id, {
            name: values.name.trim(),
            isActive: values.isActive,
          });
          if (res.success) {
            toast.success(res.message);
            onSuccess();
            formik.resetForm();
            onClose();
          }
        } else {
          const res = await createMaterial({
            name: values.name.trim(),
            isActive: values.isActive,
          });
          if (res.success) {
            toast.success(res.message);
            onSuccess();
            formik.resetForm();
            onClose();
          }
        }
      } catch (err: any) {
        toast.error(
          err?.response?.data?.message ??
            err?.message ??
            (isEdit ? "Failed to update material" : "Failed to create material"),
        );
      }
    },
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <Modal isOpen={show} toggle={handleClose} centered>
      <ModalHeader toggle={handleClose}>
        {isEdit ? "Edit material" : "Add material"}
      </ModalHeader>
      <ModalBody>
        <p className="text-muted small mb-3">
          Name must be unique. Materials referenced by checkout records or
          products cannot be deleted until unused.
        </p>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            formik.handleSubmit();
          }}
        >
          <div className="mb-3">
            <Label className="form-label" htmlFor="material-modal-name">
              Name
            </Label>
            <Input
              id="material-modal-name"
              name="name"
              type="text"
              className="form-control"
              placeholder="e.g. Steel Rod, PVC Pipe"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={!!(formik.touched.name && formik.errors.name)}
            />
            {formik.touched.name && formik.errors.name ? (
              <FormFeedback type="invalid">{formik.errors.name}</FormFeedback>
            ) : null}
          </div>
          <div className="form-check form-switch form-switch-md mb-4">
            <Input
              className="form-check-input"
              type="checkbox"
              id="material-modal-active"
              name="isActive"
              checked={formik.values.isActive}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            <Label className="form-check-label" htmlFor="material-modal-active">
              Active (shown in selectors)
            </Label>
          </div>
          <div className="d-flex gap-2 justify-content-end">
            <Button type="button" color="light" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              color="primary"
              disabled={formik.isSubmitting}
            >
              {formik.isSubmitting
                ? isEdit
                  ? "Saving…"
                  : "Creating…"
                : isEdit
                  ? "Update"
                  : "Create"}
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
}
